import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DownloadUrl } from '@app/core/model/download-url';
import { IProject } from '@app/core/model/projects/project';
import { ResourceType } from '@app/core/model/resource-type';
import { ISample } from '@app/core/model/samples/sample';
import { IApiResponseWrapper, IBulkUpdateResponse } from '@app/core/model/v2-api/v2-api-wrappers';
import { BsApiService, ISampleMergeResponse } from '@app/core/services/bs-api/bs-api-service';
import { LegacyBsApiEndpointsService } from '@app/core/services/legacy-bs-api/legacy-bs-api-endpoints.service';
import { PageContextService } from '@app/core/services/page-context.service';
import { DownloadService } from '@app/core/services/toolbar/download.service';
import { TrashService } from '@app/core/services/toolbar/trash.service';
import { UserRestrictionService } from '@app/core/services/user/user-restriction.service';
import { ArrayUtilities } from '@app/core/utilities/array-utilities';
import { ConsoleLogger } from '@app/core/utilities/consolelogger';
import { ErrorMessages } from '@app/core/utilities/error-messages';
import { ToastrMessages } from '@app/core/utilities/toastr-messages';
import { ToastrService } from '@bssh/comp-lib';
import { V1pre3TrashItem } from '@bssh/ng-sdk';
import { isEmpty } from 'lodash';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, retryWhen, shareReplay, tap } from 'rxjs/operators';
import { genericRetryWhen, observableEmitDelay } from '../../rxjsutils/rxjs-utilities';
import { AbstractResourceStore } from '../resource/abstract.resource.store';
import { IResourceStore } from '../resource/resource.store';
import { ISamplesState } from './samples.state';
import { isNullOrUndefined } from 'util';

export interface ISamplesStore extends IResourceStore<ISamplesState> {
    loadCurrentSample(id: string, forceLoad: boolean): void;
    // loadSamplesList(offset: number, limit: number, sortby: string, sortdir: string, forceLoad: boolean): void;
    updateSelectedSamples(updatedSelections: ISample[]): void;
    unloadCurrentSample(): void;
    validateMerge(projectId: string): Observable<ISampleMergeResponse>;
    merge(projectId: string, newSampleName: string): Observable<ISampleMergeResponse>;
}

// The action-names for this store, for logging/tracking.
export enum SampleStoreActions {
    UpdateSelectedSamples = 'UPDATE_SELECTED_SAMPLES',
    EditSampleName = 'EDIT_NAME',
    LoadSampleDetails = 'LOAD_SAMPLE_DETAILS',
    DeleteSamples = 'DELETE_SAMPLES'
}

@Injectable({
    providedIn: 'root'
})
export class SamplesStore extends AbstractResourceStore<ISamplesState> implements ISamplesStore {

    private refreshSubject = new BehaviorSubject<boolean>(false);
    samplesRefreshed$ = this.refreshSubject.asObservable();

    constructor(
        private pageContextService: PageContextService,
        private bsApiService: BsApiService,
        private userRestrictionService: UserRestrictionService,
        private trashService: TrashService,
        private toastrService: ToastrService,
        private downloadService: DownloadService,
        private httpClient: HttpClient,
        private legacyBsApiEndpointsService: LegacyBsApiEndpointsService
    ) {
        super(
            [
                'samplesList',
                'samplesOffset',
                'samplesLimit',
                'samplesSortBy',
                'samplesSortDir',
                'selectedSamples',
                'currentSample',
                'sampleStateError',
                'currentUserId'
            ]
        );
    }

    // method to access @refreshSubject externally to refresh current view
    forceRefreshStore() {
        this.refreshSubject.next(true);
    }

    /**
     * Calls BSSH API to load sample details and cache in the  store
     * @param id Id of the sample to fetch. Will not fetch if the state's currentSample already matches this id.
     */
    loadCurrentSample(id: string, forceLoad: boolean = false) {

        const currentState = this.getState();
        if (currentState && currentState.currentSample && currentState.currentSample.Id === id && !forceLoad) {
            // if sample already set don't fetch, but dispatch current state if another call is made later
            this.dispatchCurrentState(currentState);
        } else {
            this.loadingSubject.next(true);

            // Subscribe to retrieve
            this.subs.sink = this.bsApiService.getSample(
                id).pipe(
                    // Do not make an additional API call if any additional subscribers have subscribed later on
                    shareReplay(1),
                    // Retry in case of HTTP errors
                    retryWhen(genericRetryWhen()),
                    // To avoid a Flash of content, maintain a delay
                    delay(observableEmitDelay),
                    // Stop loading in finalize.
                    finalize(() => this.loadingSubject.next(false))
                ).subscribe({
                    next: (v2SampleDetails) => {
                        this.setState({ currentSample: v2SampleDetails.Response }, SampleStoreActions.LoadSampleDetails);
                    },
                    error: error => this.handleError(error, () => ({ ...currentState, sampleStateError: error }))
                });
        }
    }

    /**
     * Unload current sample data to avoid stale data displayed when enter another sample details page
     */
    unloadCurrentSample() {
        this.setState({ currentSample: undefined });
    }

    updateSelectedSamples(updatedSelections: ISample[]) {
        this.setState({ selectedSamples: updatedSelections }, SampleStoreActions.UpdateSelectedSamples);
    }

    getActiveList(): ISample[] {
        const { currentSample, selectedSamples } = this.getState();

        if (this.pageContextService.isSubResourceSamplesList()) {
          return selectedSamples;
        } else if(!isNullOrUndefined(currentSample)) {
          return [currentSample];
        } else {
          return null;
        }
    }

    private setStateOnSuccess(
      propertyChange: object,
      updatedSamples: ISample[],
      actionName: SampleStoreActions,
      error: any = null,
      forceRefresh?: boolean
    ) {
      const { samplesList, selectedSamples } = this.getState();

      let newSamplesList = samplesList;
      let newSelectedSamplesList = selectedSamples;

      if (updatedSamples) {
        if (!selectedSamples || selectedSamples.length === 0) {
          return;
        }

        updatedSamples.forEach(sample => {
            newSamplesList = this.getUpdatedSamplesList(sample, samplesList, propertyChange);
            newSelectedSamplesList = this.getUpdatedSamplesList(sample, selectedSamples, propertyChange);
        });
      }

      this.setState({
          samplesList: newSamplesList,
          selectedSamples: newSelectedSamplesList,
          sampleStateError: error
      }, actionName);

      this.refreshSubject.next(forceRefresh);
    }

    private getUpdatedSamplesList(sample: ISample, samplesList: ISample[], propertyUpdate: object): ISample[] {
      if (samplesList) {
          const idxSelectedSample = samplesList.findIndex(changedSample => changedSample.Id === sample.Id);
          if (idxSelectedSample !== -1) {
              Object.assign(samplesList[idxSelectedSample], propertyUpdate);
          }
      }
      return samplesList;
    }

    edit(newSampleName: string): void {
        if (!this.canEdit()) {
            return;
        }
    }

    canEdit(): boolean {
        const activeSamples = this.getActiveList();
        return activeSamples &&
            ArrayUtilities.isArrayOfSize(activeSamples, 1) &&
            this.isResourceOwner(activeSamples[0]);
    }

    copySamplesToProject(originProjectId: string, destinationProjectId, sampleIds: number[], ): Observable<any> {
      const url = this.legacyBsApiEndpointsService.getCopySamplesToProjectUrl(originProjectId);
      return this.httpClient.put<any>(
        url,
        { action: 'copy', destinationProjectId: destinationProjectId, samples: sampleIds }
      );
    }

    canCopy(): boolean {
      return !isEmpty(this.getState().selectedSamples) && !this.userRestrictionService.userHasStorageRestrictions();
    }

    copy(destinationProject: IProject, originProject: IProject): void {
      if (!originProject || !destinationProject) {
          return;
      }
      this.loadingSubject.next(true);
      const selectedSampleIds = this.getActiveList().map((sample) => parseInt(sample.Id, 10));

      // using this api as suppose to PostV2Appsessions bcs of mandatory params name and id
      this.subs.sink = this.copySamplesToProject(originProject.Id, destinationProject.Id, selectedSampleIds).pipe(
        finalize(() => this.loadingSubject.next(false))
      ).subscribe({
          next: (response: any) => {
            // showing toaster message on success
            this.toastrService.success(ToastrMessages.COPY_SAMPLES_MESSAGE(destinationProject.Name, selectedSampleIds.length));
          },
          error: (error: any) => {

            if(error.error && error.error.errors && error.error.errors[0] && error.error.errors[0].ErrorMessage) {
              this.toastrService.error(error.error.errors[0].ErrorMessage);
            }
            this.handleError(error, () => ({ ...this.getState(), sampleStateError: error }));
          }
      });
    }

    canTrash(): boolean {
        const activeSamples = this.getActiveList();
        const { currentUserId } = this.getState();

        return this.trashService.canTrash(activeSamples, currentUserId);
    }

    /**
     * Calls the TrashService to Trash the Samples after checking the status
     */
     trash(): void {
        const activeSamples = this.getActiveList();

        if (!activeSamples) {
          throw new Error(ErrorMessages.MISSING_RESOURCE_ERROR);
        }

        const sampleIds = activeSamples.map(sample => sample.Id);
        this.trashSamples(sampleIds, activeSamples);
      }

    public trashSamples(sampleIds: string[], samplesToBeTrashed: ISample[]): void {
      this.loadingSubject.next(true);

      // trash sample of recieved ids
      this.subs.sink = this.trashService.trashSamples(sampleIds).pipe(
        finalize(() => this.loadingSubject.next(false))
      ).subscribe({
        next: (sampleTrashResponse) => {
          this.handleTrashSuccess(samplesToBeTrashed, sampleTrashResponse);
        },
        error: (error) => {
          this.handleError(error, () => ({ ...this.getState(), sampleStateError: error }));
        }
      })
    }

    private handleTrashSuccess(
      samplesToBeTrashed: ISample[],
      sampleTrashResponse: IBulkUpdateResponse<IApiResponseWrapper<V1pre3TrashItem>>
    ): void {
      const totalSuccessCount = sampleTrashResponse.TotalSuccessCount;
      const totalFailureCount = sampleTrashResponse.TotalFailureCount;

      // getting analyses to update that were successfully trashed
      const deletedSamples: ISample[] = [];
      if (totalSuccessCount > 0) {
        sampleTrashResponse.SuccessItems.forEach(item => {
          const samples = samplesToBeTrashed.find(sample => sample.Id === item.Response.DeletedSample.Id);
          deletedSamples.push(samples);
        });
      }

      let trashError = null;
      if (totalFailureCount > 0) {
        trashError = [];
        // load trash error with samples error,
        // only app session errors will be there
        sampleTrashResponse.FailureItems.forEach(item => {
          trashError.push({
            errorMessage: item.ErrorMessage,
            data : item,
          });
        });
      }

      this.setStateOnSuccess(
        { IsDeleted: true },
        deletedSamples,
        SampleStoreActions.DeleteSamples,
        trashError,
        totalSuccessCount > 0
      );

      // showing toaster message on success only. Errors are shown in modal itself
      if ((!trashError || trashError.length === 0)) {
          this.toastrService.success(ToastrMessages.TRASH_SAMPLES_MESSAGE(totalSuccessCount));
      }
    }
    canDownload(): boolean {
        const selectedSamples = this.getActiveList();
        return selectedSamples && selectedSamples.length > 0;
    }

    // Calls BSSH API to get Sample Download Uri
    // Can download 1 or more samples
    getDownloadUri(): Observable<DownloadUrl> {
        this.loadingSubject.next(true);
        const activeSamples = this.getActiveList();
        const sampleIds = activeSamples.map((sample: ISample) => sample.Id);

        return this.downloadService.getDownloadUri(sampleIds, ResourceType.SAMPLE).pipe(
            finalize(() => {
                this.loadingSubject.next(false);
            })
        );
    }

    canMerge(): boolean {
        const activeSamples = this.getActiveList();
        return activeSamples &&
            activeSamples.length > 1 &&
            activeSamples.every((sample: ISample) => this.isResourceOwner(sample));
    }

    validateMergeSamples(projectId: string, sampleIds: string[]): Observable<ISampleMergeResponse> {
        const requestUrl = this.legacyBsApiEndpointsService.getMergeSamplesUrl(projectId);

        const payload = {
          action: 'validate',
          samplename: '',
          samples: sampleIds
        };

        return this.httpClient.put<any>(requestUrl, payload).pipe(
          // Retry in case of HTTP errors
          retryWhen(genericRetryWhen()),
          // To avoid a Flash of content, maintain a delay
          delay(observableEmitDelay),
        );
    }

    validateMerge(projectId: string): Observable<ISampleMergeResponse> {
        this.loadingSubject.next(true);
        const activeSamples = this.getActiveList();
        const sampleIds = activeSamples.map((sample: ISample) => sample.Id);

        return this.validateMergeSamples(projectId, sampleIds).pipe(
            finalize(() => {
                this.loadingSubject.next(false);
            })
        );
    }

    mergeSamples(projectId: string, sampleIds: string[], newSampleName: string): Observable<ISampleMergeResponse> {
        const requestUrl = this.legacyBsApiEndpointsService.getMergeSamplesUrl(projectId);

        const payload = {
          action: 'merge',
          samplename: newSampleName,
          samples: sampleIds
        };

        return this.httpClient.put<any>(requestUrl, payload).pipe(
          // Retry in case of HTTP errors
          retryWhen(genericRetryWhen()),
          // To avoid a Flash of content, maintain a delay
          delay(observableEmitDelay),
        );
    }

    merge(projectId: string, newSampleName: string): Observable<ISampleMergeResponse> {
        this.loadingSubject.next(true);
        const activeSamples = this.getActiveList();
        const sampleIds = activeSamples.map((sample: ISample) => sample.Id);

        return this.mergeSamples(projectId, sampleIds, newSampleName).pipe(
            tap((response: ISampleMergeResponse) => {
                if(response.errors && response.errors.length > 0) {
                    this.toastrService.error(response.errors[0].ErrorMessage);
                } else {
                    const sampleNames = activeSamples.map((sample: ISample) => sample.SampleId);
                    this.toastrService.success(ToastrMessages.MERGE_SAMPLES_SUCCESS(sampleNames));

                    // Refresh samples list
                    this.refreshSubject.next(true);
                }
            }),
            catchError((error) => {
                ConsoleLogger.logMessage(error);
                this.toastrService.error('Due to a server error, we were unable to combine your samples.');
                return throwError(error);
            }),
            finalize(() => {
                this.loadingSubject.next(false);
            })
        );
    }

    getResourceType(): ResourceType {
        return ResourceType.SAMPLES;
    }
}

